import { HEX_ALPHA, cyan, grey } from "@faro-lotv/flat-ui";
import { Box, alpha } from "@mui/material";
import { type JSX } from "react";

/** The timeline's rail: its width, line, and the node marking each month on it */
const RAIL_WIDTH = "28px";
const RAIL_LINE_WIDTH = "2px";
const MONTH_NODE_SIZE = "10px";
const END_NODE_SIZE = "8px";

type TimelineRailProps = {
  isMonth: boolean;
  isFirst: boolean;
  isLast: boolean;
  isSolid: boolean;
};

/**
 * @returns The left edge of a timeline row: a line joining the rows, through a node on a month
 * @param props Which row the rail belongs to and whether it sits on a solid range end
 */
export function TimelineRail({
  isMonth,
  isFirst,
  isLast,
  isSolid,
}: TimelineRailProps): JSX.Element {
  const line = isSolid ? alpha(grey[950], HEX_ALPHA.alpha66) : grey[700];

  return (
    <Box
      component="span"
      sx={{
        position: "relative",
        alignSelf: "stretch",
        flexShrink: 0,
        width: RAIL_WIDTH,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        component="span"
        sx={{
          position: "absolute",
          left: `calc(50% - ${RAIL_LINE_WIDTH} / 2)`,
          width: RAIL_LINE_WIDTH,
          top: isFirst ? "50%" : 0,
          bottom: isLast ? "50%" : 0,
          backgroundColor: line,
        }}
      />
      {isMonth && (
        <Box
          component="span"
          sx={{
            position: "relative",
            width: MONTH_NODE_SIZE,
            height: MONTH_NODE_SIZE,
            borderRadius: "50%",
            backgroundColor: isSolid ? grey[950] : cyan[400],
            border: `${RAIL_LINE_WIDTH} solid ${line}`,
          }}
        />
      )}
      {/* Days carry no node, but the timeline's oldest end needs one not to stop abruptly */}
      {isLast && !isMonth && (
        <Box
          component="span"
          sx={{
            position: "relative",
            width: END_NODE_SIZE,
            height: END_NODE_SIZE,
            borderRadius: "50%",
            backgroundColor: isSolid ? grey[950] : grey[500],
          }}
        />
      )}
    </Box>
  );
}
